import { type FC, type ReactNode, memo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
// Import Types
import type { SchemaDependency } from 'types';
// Import UI Components
import { Popover } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import styles from './FilterDependencyDots.module.scss';

const DOT_RADIUS = 2.5;

const POSITION_MAP: Record<number, { cx: number; cy: number }[]> = {
    1: [{ cx: 10, cy: 10 }],
    2: [
        { cx: 10, cy: 6 },
        { cx: 10, cy: 14 },
    ],
    3: [
        { cx: 10, cy: 7 },
        { cx: 6, cy: 13 },
        { cx: 14, cy: 13 },
    ],
    4: [
        { cx: 6, cy: 6 },
        { cx: 14, cy: 6 },
        { cx: 6, cy: 14 },
        { cx: 14, cy: 14 },
    ],
    5: [
        { cx: 6, cy: 6 },
        { cx: 14, cy: 6 },
        { cx: 6, cy: 14 },
        { cx: 14, cy: 14 },
        { cx: 10, cy: 10 },
    ],
    6: [
        { cx: 7, cy: 5 },
        { cx: 13, cy: 5 },
        { cx: 7, cy: 10 },
        { cx: 13, cy: 10 },
        { cx: 7, cy: 15 },
        { cx: 13, cy: 15 },
    ],
};

export type DependencyPopoverPosition = 'left' | 'right';

interface FilterDependencyDotsPopoverProps {
    schemaDeps: SchemaDependency[];
    isDragging?: boolean;
    className?: string;
    popoverPosition: DependencyPopoverPosition;
    children: ReactNode;
}

const FilterDependencyDotsPopover: FC<FilterDependencyDotsPopoverProps> = ({
    schemaDeps,
    isDragging,
    className,
    popoverPosition,
    children,
}) => {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Popover
            isOpen={isHovered && !isDragging}
            extendTargetWidth={false}
            scrollbarNeeded={false}
            position={popoverPosition}
            className={styles.hoverPopover}
            Content={
                <div className={styles.popoverContent}>
                    <span className={styles.popoverTitle}>{t(l.Dependencies)}</span>
                    <ul className={styles.dependencyPopoverList}>
                        {schemaDeps.map((dep) => (
                            <li key={dep.id} className={styles.dependencyPopoverItem}>
                                <span className={styles.dependencyPopoverDot} style={{ backgroundColor: dep.color }} />
                                <span className={styles.dependencyPopoverName}>{dep.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            }
        >
            <div
                className={classnames(styles.dependencyDotsTile, className)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {children}
            </div>
        </Popover>
    );
};

export interface FilterDependencyDotsProps {
    /** Data tables the filter/KPI depends on; 1 to 6 entries are rendered, anything else renders nothing. */
    schemaDeps: SchemaDependency[];
    /** Hides the hover popover while the owner is being dragged. */
    isDragging?: boolean;
    /** Extra class for the 20x20 tile wrapper (lets a page keep its own tile look). */
    className?: string;
    /** Side the hover popover opens on; use `left` for tiles near the right edge of the viewport. */
    popoverPosition?: DependencyPopoverPosition;
}

/**
 * 20x20 tile with one colored dot per data table (schema) the filter/KPI depends on,
 * plus a hover popover listing the dependency names.
 */
const FilterDependencyDots: FC<FilterDependencyDotsProps> = ({
    schemaDeps,
    isDragging,
    className,
    popoverPosition = 'right',
}) => {
    const positions = POSITION_MAP[schemaDeps.length];

    if (!positions) return null; // Only 1..6 dependencies are supported for visual clarity

    return (
        <FilterDependencyDotsPopover
            schemaDeps={schemaDeps}
            isDragging={isDragging}
            className={className}
            popoverPosition={popoverPosition}
        >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect width="20" height="20" rx="5" />
                {positions.map((pos, index) => (
                    <circle
                        key={schemaDeps[index].id}
                        cx={pos.cx}
                        cy={pos.cy}
                        r={DOT_RADIUS}
                        fill={schemaDeps[index].color}
                    />
                ))}
            </svg>
        </FilterDependencyDotsPopover>
    );
};

export default memo(FilterDependencyDots);
